import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { CheckCircle2, Circle, Plus, Trash2, ListChecks, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MyChecklistWidget — personal to-do list on every user's dashboard.
 *
 * Backed by /api/my-todos (canonical user_todos collection). Shows the
 * top open items, lets the user add / toggle / delete inline. Click the
 * title to jump to the full /my-todos page.
 */
export default function MyChecklistWidget() {
  const navigate = useNavigate();
  const [todos, setTodos] = useState([]);
  const [stats, setStats] = useState({ total: 0, urgent: 0, completed: 0, overdue: 0 });
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const authHeader = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const fetchTodos = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/my-todos?include_completed=true`, { headers: authHeader });
      if (!r.ok) throw new Error('Failed to load');
      const data = await r.json();
      setTodos(data.todos || []);
      setStats(data.stats || {});
    } catch (e) {
      // Silent — widget shows empty state
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { fetchTodos(); }, [fetchTodos]);

  const addTodo = async () => {
    const title = draft.trim();
    if (!title || saving) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/my-todos`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, status: 'urgent' }),
      });
      if (!r.ok) throw new Error('Failed to add');
      setDraft('');
      await fetchTodos();
    } catch (e) {
      toast.error('Could not add item');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (todo) => {
    const nextStatus = todo.status === 'completed' ? 'urgent' : 'completed';
    // Optimistic update
    setTodos(prev => prev.map(t => t.id === todo.id ? { ...t, status: nextStatus } : t));
    try {
      const r = await fetch(`${API_URL}/api/my-todos/${todo.id}`, {
        method: 'PUT',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!r.ok) throw new Error('Failed');
      fetchTodos();
    } catch (e) {
      // Revert on failure
      setTodos(prev => prev.map(t => t.id === todo.id ? { ...t, status: todo.status } : t));
      toast.error('Could not update');
    }
  };

  const remove = async (todo) => {
    setTodos(prev => prev.filter(t => t.id !== todo.id));
    try {
      await fetch(`${API_URL}/api/my-todos/${todo.id}`, { method: 'DELETE', headers: authHeader });
      fetchTodos();
    } catch (e) {
      toast.error('Could not delete');
      fetchTodos();
    }
  };

  const toggleStar = async (todo) => {
    const next = !todo.starred;
    setTodos(prev => prev.map(t => t.id === todo.id ? { ...t, starred: next } : t));
    try {
      await fetch(`${API_URL}/api/my-todos/${todo.id}`, {
        method: 'PUT',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ starred: next }),
      });
    } catch (_) {
      setTodos(prev => prev.map(t => t.id === todo.id ? { ...t, starred: todo.starred } : t));
    }
  };

  // Show top 6 — starred + open first, then completed at bottom
  const visible = useMemo(() => {
    const open = todos.filter(t => t.status !== 'completed');
    const done = todos.filter(t => t.status === 'completed');
    return [...open, ...done].slice(0, 6);
  }, [todos]);

  const openCount = stats.total - (stats.completed || 0);

  return (
    <Card className="h-full flex flex-col" data-testid="my-checklist-widget">
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2 border-b">
        <button
          onClick={() => navigate('/my-todos')}
          className="flex items-center gap-2 text-left hover:text-red-600 transition-colors min-w-0"
        >
          <span className="w-7 h-7 rounded-md bg-red-50 text-red-600 flex items-center justify-center shrink-0">
            <ListChecks className="h-4 w-4" />
          </span>
          <CardTitle className="text-sm font-bold tracking-tight truncate">My Checklist</CardTitle>
        </button>
        <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500 tabular-nums shrink-0">
          {openCount} open · {stats.completed || 0} done
        </span>
      </CardHeader>

      <CardContent className="p-3 flex-1 flex flex-col gap-2 overflow-hidden">
        {/* Add bar */}
        <div className="flex items-center gap-1.5">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addTodo(); }}
            placeholder="Add a task and press Enter…"
            className="h-8 text-sm border-slate-300"
            disabled={saving}
            data-testid="checklist-add-input"
            maxLength={200}
          />
          <Button
            onClick={addTodo}
            disabled={!draft.trim() || saving}
            size="sm"
            className="h-8 px-2 bg-red-600 hover:bg-red-700 text-white shrink-0"
            data-testid="checklist-add-btn"
          ><Plus className="h-4 w-4" /></Button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1">
          {loading ? (
            <div className="space-y-1.5 pt-1">
              {[0, 1, 2].map(i => <div key={i} className="h-8 bg-slate-100 animate-pulse rounded" />)}
            </div>
          ) : visible.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-6">
              <ListChecks className="h-7 w-7 text-slate-300 mb-1.5" />
              <p className="text-xs text-slate-500 font-medium">Nothing on your list.</p>
              <p className="text-[11px] text-slate-400">Type above to add your first task.</p>
            </div>
          ) : visible.map(todo => {
            const done = todo.status === 'completed';
            return (
              <div
                key={todo.id}
                className={`group flex items-center gap-2 px-1.5 py-1.5 rounded hover:bg-slate-50 transition-colors ${done ? 'opacity-60' : ''}`}
                data-testid={`checklist-item-${todo.id}`}
              >
                <button
                  onClick={() => toggle(todo)}
                  className="shrink-0 transition-colors"
                  title={done ? 'Mark as open' : 'Mark as done'}
                  data-testid={`checklist-toggle-${todo.id}`}
                >
                  {done ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-400 hover:text-red-500" />}
                </button>
                <span className={`flex-1 text-sm leading-snug truncate ${done ? 'line-through text-slate-400' : 'text-slate-800'}`}>
                  {todo.title}
                </span>
                {todo.due_date && !done && (
                  <span className="text-[10px] uppercase tracking-wider font-bold text-amber-600 tabular-nums shrink-0">
                    {todo.due_date.slice(5)}
                  </span>
                )}
                <button
                  onClick={() => toggleStar(todo)}
                  className={`shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${todo.starred ? 'opacity-100' : ''}`}
                  title={todo.starred ? 'Unstar' : 'Star'}
                >
                  <Star className={`h-3.5 w-3.5 ${todo.starred ? 'fill-amber-400 text-amber-500' : 'text-slate-400 hover:text-amber-500'}`} />
                </button>
                <button
                  onClick={() => remove(todo)}
                  className="shrink-0 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-all"
                  title="Delete"
                  data-testid={`checklist-delete-${todo.id}`}
                ><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            );
          })}
        </div>

        {todos.length > visible.length && (
          <button
            onClick={() => navigate('/my-todos')}
            className="text-[11px] uppercase tracking-wider font-bold text-red-600 hover:text-red-700 pt-1 text-left"
          >View all {todos.length} →</button>
        )}
      </CardContent>
    </Card>
  );
}
